/**
 * useComposeProposalChat — the composer's AI chat, powered by the proposal-
 * discussion agent.
 *
 * The composer's draft lives on a PENDING gmail_send proposal, and each chat
 * turn is the existing proposal discuss flow: the same reasoning agent that
 * drafts and refines emails in AI Actions edits this draft via its
 * update_proposed_action tool, fetching recipient/CRM/email context through
 * its own tools. Nothing about drafting or context assembly is reimplemented
 * here — this hook is pure UI orchestration:
 *
 *   ensure pending proposal (lazy, first turn)
 *     → sync editor edits onto the action (update_payload, only when dirty)
 *     → POST discuss → poll progress (useProposalPolling)
 *     → refetch proposal → hand the agent's revised draft back to the editor.
 *
 * The proposal uses the `ahoy_compose` interactive channel, so while pending it
 * never surfaces in the Actions inbox. Send approves it (useSendComposedEmail);
 * discarding the composer deletes it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ComposedEmail } from '@/lib/compose/useSendComposedEmail';
import {
  buildComposeProposalRequest,
  buildGmailSendParams,
} from '@/lib/compose/useSendComposedEmail';
import { logger } from '@/lib/logger';
import type { EmailContent } from '@/lib/proposals/core/emailContent';
import { emailContentFromGmailSend } from '@/lib/proposals/core/emailContent';
import { ProposalClient } from '@/lib/proposals/core/ProposalClient';
import type {
  GmailSendPayload,
  ProposedAction,
} from '@/lib/proposals/core/types';
import { isGmailSendPayload } from '@/lib/proposals/core/types';
import type { ReasoningStepSummary } from '@/lib/proposals/hooks/latestStepSummary';
import { useProposalPolling } from '@/lib/proposals/hooks/useProposalPolling';

export interface ComposeChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

/** Same DB-commit-race delay ProposalDetailPanel uses before refetching. */
const REFETCH_DELAY_MS = 500;

export const GENERIC_TURN_ERROR =
  "The AI couldn't update your draft. Please try again.";

interface UseComposeProposalChatOptions {
  orgId: number;
  currentUserId: number;
  recipientEntityId?: string;
  /** Reads the LATEST editor content at call time (must be ref-backed). */
  getContent: () => Omit<ComposedEmail, 'proposalId' | 'actionId'>;
  /** Apply the agent's revised draft to the editor (remount included). */
  onAgentDraft: (email: EmailContent) => void;
}

export interface UseComposeProposalChat {
  messages: ComposeChatMessage[];
  /** True from send until the agent turn fully lands (or fails). */
  isBusy: boolean;
  /** Live "what the agent is doing" text while a turn runs. */
  progressDescription: string | null;
  error: string | null;
  /** Send an instruction; resolves false (input kept) when nothing started. */
  sendMessage: (instruction: string) => Promise<boolean>;
  /** Set once the first chat turn created the pending proposal. */
  proposalId: number | null;
  actionId: number | null;
  /** Stop polling and silently delete the pending proposal (discard). */
  discardProposal: () => Promise<void>;
  /**
   * Hand off ownership of the pending proposal WITHOUT deleting it — call after
   * a successful Send (which approves the same proposal) so the unmount cleanup
   * does not delete an already-approved proposal.
   */
  releaseProposal: () => void;
}

/** Map an applied EmailContent back into builder input for dirty-comparison. */
function composedFromEmailContent(
  email: EmailContent,
  recipientEntityId: string | undefined
): Omit<ComposedEmail, 'proposalId' | 'actionId'> {
  return {
    to: email.to,
    cc: email.cc ?? [],
    bcc: email.bcc ?? [],
    subject: email.subject,
    body: email.body,
    body_html: email.body_html,
    attachment_file_ids: email.attachment_file_ids ?? [],
    social_account_id: email.social_account_id,
    disable_tracking: email.disable_tracking,
    signature_disposition: email.signature_disposition,
    recipientEntityId,
  };
}

export function useComposeProposalChat(
  options: UseComposeProposalChatOptions
): UseComposeProposalChat {
  const { orgId, currentUserId, recipientEntityId } = options;

  const client = useMemo(() => new ProposalClient(orgId), [orgId]);
  // The ProposalClient that OWNS the currently-pending proposal — captured at
  // creation time and used for every later operation on it (sync, discuss,
  // refetch, delete). `client` is recreated whenever `orgId` changes (an org
  // switch while the composer stays mounted; the panel is keyed on openSeq, not
  // orgId), but a pending proposal stays bound to the org it was created in.
  // Routing its DELETE/refetch through the latest org's client would hit the
  // wrong org and orphan the proposal. Null whenever no proposal is pending.
  const owningClientRef = useRef<ProposalClient | null>(null);
  // The org the currently-pending proposal was created in. Progress polling is
  // pinned to THIS org for the whole turn's lifetime, never the live `orgId`:
  // if the composer's org switches mid-turn, `orgId` would point the poll at a
  // different org whose 404 for this task usePolling treats as completion —
  // prematurely applying a stale draft/reply. Kept in state (not just a ref)
  // because it is a polling input and must survive the org-switch re-render.
  // Set the moment a proposal becomes owned; cleared on discard/release.
  const [owningOrgId, setOwningOrgId] = useState<number | null>(null);

  const [messages, setMessages] = useState<ComposeChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [proposalId, setProposalId] = useState<number | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);

  // Refs for values read inside async flows/poll callbacks (stale-closure
  // prevention) — kept in sync each render.
  const proposalIdRef = useRef<number | null>(null);
  const actionIdRef = useRef<number | null>(null);
  /** JSON of the last action_params synced to the proposal (dirty check). */
  const lastSyncedParamsRef = useRef<string | null>(null);
  const getContentRef = useRef(options.getContent);
  getContentRef.current = options.getContent;
  const onAgentDraftRef = useRef(options.onAgentDraft);
  onAgentDraftRef.current = options.onAgentDraft;
  /** Poll state at completion time, read inside handleComplete. */
  const latestStepRef = useRef<ReasoningStepSummary | null>(null);
  /**
   * The task id of the turn currently in flight. useProposalPolling fires
   * onComplete on EVERY poll tick whose response reports completion (there is
   * no once-guard, and in-flight ticks can land after setTaskId(null) but
   * before polling actually stops) — this ref is cleared synchronously on the
   * first terminal event so duplicates no-op instead of appending the same
   * assistant reply repeatedly.
   */
  const activeTaskRef = useRef<string | null>(null);
  /**
   * Monotonic turn fence. Bumped at the start of each send AND on discard, so
   * an async continuation (after create/update/discuss awaits) can detect that
   * a discard landed mid-flight and bail — instead of repopulating the
   * proposal refs or starting polling for a proposal that is already being
   * deleted. Without it, discarding while the first turn is still creating its
   * proposal leaves an orphaned pending proposal and a post-discard agent turn.
   */
  const turnSeqRef = useRef(0);

  const appendMessage = useCallback(
    (role: ComposeChatMessage['role'], content: string): string => {
      const id = crypto.randomUUID();
      setMessages(prev => [...prev, { id, role, content }]);
      return id;
    },
    []
  );

  // --- poll completion -----------------------------------------------------

  const handleComplete = useCallback(() => {
    // Duplicate completion tick for a turn already handled — ignore.
    if (activeTaskRef.current === null) {
      return;
    }
    activeTaskRef.current = null;
    setTaskId(null);
    const pid = proposalIdRef.current;
    if (pid === null) {
      setIsSending(false);
      return;
    }
    // Snapshot the turn fence: discardProposal bumps turnSeqRef, so if a discard
    // lands during the delay/refetch below we detect the mismatch and bail
    // instead of applying a deleted turn's draft or appending its reply.
    const seq = turnSeqRef.current;
    void (async () => {
      try {
        // Same commit-race delay the proposal panel uses before refetching.
        await new Promise(resolve => setTimeout(resolve, REFETCH_DELAY_MS));
        // Refetch through the proposal's owning client — not `client`, which may
        // point at a different org after a switch.
        const owningClient = owningClientRef.current ?? client;
        const detail = await owningClient.get(pid);
        if (turnSeqRef.current !== seq) {
          // Discarded (or superseded) mid-refetch — don't mutate the composer.
          return;
        }
        const action = detail.actions?.find(candidate =>
          isGmailSendPayload(candidate.payload)
        );
        if (action) {
          actionIdRef.current = action.id;
          setActionId(action.id);
          const email = emailContentFromGmailSend(
            action as ProposedAction<GmailSendPayload>
          );
          // Best-effort resync of the dirty-check baseline to the applied
          // draft; a mismatch only costs one harmless extra update_payload
          // on the next turn.
          lastSyncedParamsRef.current = JSON.stringify(
            buildGmailSendParams(
              composedFromEmailContent(email, recipientEntityId),
              { placeholders: true }
            )
          );
          onAgentDraftRef.current(email);
        }
        const step = latestStepRef.current;
        appendMessage(
          'assistant',
          step?.content?.trim() || 'I updated your draft.'
        );
      } catch (e) {
        logger.error(
          '[useComposeProposalChat] post-turn refetch failed',
          e instanceof Error ? e : undefined
        );
        setError(GENERIC_TURN_ERROR);
      } finally {
        setIsSending(false);
      }
    })();
  }, [appendMessage, client, recipientEntityId]);

  const handleError = useCallback((pollError: Error) => {
    // Duplicate terminal tick for a turn already handled — ignore.
    if (activeTaskRef.current === null) {
      return;
    }
    activeTaskRef.current = null;
    logger.error('[useComposeProposalChat] discussion turn failed', pollError);
    setTaskId(null);
    setIsSending(false);
    setError(GENERIC_TURN_ERROR);
  }, []);

  const pollingOptions = useMemo(
    () => ({ onComplete: handleComplete, onError: handleError }),
    [handleComplete, handleError]
  );

  const { isPolling, latestStep, progressDescription } = useProposalPolling(
    taskId,
    conversationId !== null ? String(conversationId) : null,
    // Poll the proposal's owning org, not the live `orgId` — an org switch
    // mid-turn must not redirect polling to an org that 404s (read as
    // "completed") this task. Falls back to `orgId` only when nothing is
    // pending (polling is disabled then, so the value is inert).
    String(owningOrgId ?? orgId),
    pollingOptions
  );

  useEffect(() => {
    latestStepRef.current = latestStep;
  }, [latestStep]);

  // --- sending -------------------------------------------------------------

  const sendMessage = useCallback(
    async (instruction: string): Promise<boolean> => {
      const text = instruction.trim();
      if (!text || isSending || isPolling) {
        return false;
      }
      const content = getContentRef.current();
      if (content.to.length === 0) {
        setError(
          'Add at least one recipient so the AI knows who this email is to.'
        );
        return false;
      }

      const bubbleId = appendMessage('user', text);
      setIsSending(true);
      setError(null);
      // Claim this turn's fence value; discardProposal bumps turnSeqRef, so any
      // await below that resolves after a discard sees the mismatch and bails.
      const seq = ++turnSeqRef.current;
      let orphanProposalId: number | null = null;

      try {
        let pid = proposalIdRef.current;
        let aid = actionIdRef.current;
        const composed = { ...content, recipientEntityId };

        if (pid === null || aid === null) {
          // First turn: lazily create the pending proposal from the current
          // draft (backend-only placeholders satisfy SendEmailParams
          // validation on a not-yet-written draft).
          const created = await client.create(
            buildComposeProposalRequest(composed, currentUserId, {
              placeholders: true,
            })
          );
          if (turnSeqRef.current !== seq) {
            // Discarded mid-create: discardProposal ran while proposalIdRef was
            // still null, so it couldn't delete this proposal. Clean up the
            // orphan ourselves and bail before repopulating any refs.
            void client.delete(created.id).catch(() => {});
            return false;
          }
          orphanProposalId = created.id;
          const createdAction = created.actions?.[0];
          if (!createdAction) {
            throw new Error(GENERIC_TURN_ERROR);
          }
          pid = created.id;
          aid = createdAction.id;
          proposalIdRef.current = pid;
          actionIdRef.current = aid;
          // Bind the creating client to this proposal so every later operation
          // (sync/discuss/refetch/delete) stays scoped to its org even if the
          // composer's orgId changes underneath us. Pin polling to the same org
          // for the turn's lifetime (see owningOrgId).
          owningClientRef.current = client;
          setOwningOrgId(client.orgId);
          setProposalId(pid);
          setActionId(aid);
          orphanProposalId = null;
          lastSyncedParamsRef.current = JSON.stringify(
            buildGmailSendParams(composed, { placeholders: true })
          );
        } else {
          // Later turns: the agent must edit the user's LATEST draft — sync
          // editor changes onto the action first, but only when dirty.
          const params = buildGmailSendParams(composed, { placeholders: true });
          const serialized = JSON.stringify(params);
          if (serialized !== lastSyncedParamsRef.current) {
            // The proposal is already owned by the client that created it (a
            // prior turn); sync onto it through that client, not the current
            // `client`, which may be scoped to a different org after a switch.
            const synced = await (
              owningClientRef.current ?? client
            ).updateActionPayload(pid, aid, {
              payload: { action_params: params },
            });
            if (turnSeqRef.current !== seq) {
              // Discarded during the sync — discardProposal already deleted the
              // proposal; don't proceed to discuss it.
              return false;
            }
            // A snapshot edit past the versioning window creates a NEW action
            // row with a new id (see actions update_payload). Adopt it so this
            // turn's discuss — and every later turn and Send — targets the
            // current version instead of the superseded action.
            if (synced.id !== aid) {
              aid = synced.id;
              actionIdRef.current = synced.id;
              setActionId(synced.id);
            }
            lastSyncedParamsRef.current = serialized;
          }
        }

        const result = await (owningClientRef.current ?? client).discuss(
          pid,
          text
        );
        if (turnSeqRef.current !== seq) {
          // Discarded during discuss — don't start polling a proposal that is
          // being (or has been) deleted.
          return false;
        }
        activeTaskRef.current = result.task_id;
        setConversationId(result.conversation_id);
        setTaskId(result.task_id);
        // isSending stays true until the poll completes/fails — isBusy covers
        // the whole turn without a gap before the first poll tick.
        return true;
      } catch (e) {
        if (orphanProposalId !== null) {
          void client.delete(orphanProposalId).catch(() => {});
        }
        setMessages(prev => prev.filter(message => message.id !== bubbleId));
        console.error(e);
        setError(GENERIC_TURN_ERROR);
        setIsSending(false);
        return false;
      }
    },
    [
      appendMessage,
      client,
      currentUserId,
      isPolling,
      isSending,
      recipientEntityId,
    ]
  );

  // --- discard -------------------------------------------------------------

  const discardProposal = useCallback(async (): Promise<void> => {
    // Fence any in-flight turn: a create/update/discuss continuation that
    // resolves after this runs sees the bumped seq and bails instead of
    // repopulating refs or starting polling.
    turnSeqRef.current += 1;
    activeTaskRef.current = null;
    setTaskId(null);
    setIsSending(false);
    const pid = proposalIdRef.current;
    // Delete through the proposal's owning client (its original org), never the
    // current `client`, which may have been recreated for a different org.
    const owningClient = owningClientRef.current ?? client;
    proposalIdRef.current = null;
    owningClientRef.current = null;
    actionIdRef.current = null;
    lastSyncedParamsRef.current = null;
    setProposalId(null);
    setActionId(null);
    setOwningOrgId(null);
    if (pid !== null) {
      try {
        // Silent hard delete; an in-flight agent turn will fail server-side
        // against the deleted row, which is harmless.
        await owningClient.delete(pid);
      } catch (e) {
        logger.warn('[useComposeProposalChat] discard delete failed', {
          message: e instanceof Error ? e.message : String(e),
        });
      }
    }
  }, [client]);

  // --- release (post-send hand-off) ----------------------------------------

  const releaseProposal = useCallback(() => {
    // The proposal was approved by Send — relinquish ownership WITHOUT deleting.
    // Fence any in-flight turn and clear the refs so the unmount cleanup below
    // finds nothing to delete (a just-sent proposal must never be hard-deleted).
    turnSeqRef.current += 1;
    activeTaskRef.current = null;
    setTaskId(null);
    setIsSending(false);
    proposalIdRef.current = null;
    owningClientRef.current = null;
    actionIdRef.current = null;
    lastSyncedParamsRef.current = null;
    setProposalId(null);
    setActionId(null);
    setOwningOrgId(null);
  }, []);

  // --- unmount cleanup -----------------------------------------------------

  // Safety net for unmounts that skip the explicit discard/send paths — e.g.
  // the composer being replaced by a newly-opened draft, a route change, or an
  // error boundary. Without it, the still-pending ahoy_compose proposal is
  // orphaned and an in-flight agent turn keeps running against a replaced panel.
  // discardProposal and releaseProposal both null proposalIdRef synchronously
  // before unmount, so this deletes ONLY a proposal neither path claimed.
  useEffect(() => {
    return () => {
      turnSeqRef.current += 1;
      activeTaskRef.current = null;
      const pid = proposalIdRef.current;
      // Delete through the proposal's owning client, scoped to the org it was
      // created in — NOT the latest render's `client`, which may have been
      // recreated for a different org.
      const owningClient = owningClientRef.current;
      proposalIdRef.current = null;
      owningClientRef.current = null;
      actionIdRef.current = null;
      if (pid !== null && owningClient !== null) {
        void owningClient.delete(pid).catch(() => {});
      }
    };
  }, []);

  return {
    messages,
    isBusy: isSending || isPolling,
    progressDescription,
    error,
    sendMessage,
    proposalId,
    actionId,
    discardProposal,
    releaseProposal,
  };
}
